/**
 * Options for the cost overview filter bar, in the shape gpd-filter expects ({ label, value }).
 *
 * The page stores its filters as the same 0/1/2 codes the segmented switches used, so the
 * helpers in beauftragt-filter.ts and amount-basis.ts keep working untouched - and anyone who
 * had a filter set before this bar existed still finds it set afterwards. These functions are
 * only the translation between a code and the array of ticked values a gpd-filter hands back.
 */

export interface FilterItem {
  label: string;
  value: number;
  subtitle?: string;
}

/**
 * "Meine Ansicht" (#4926): the planning costs circles - same codes, same wording - plus the
 * project list's "Meine Projekte". Several ticked = any of them; nothing ticked = every cost.
 */
export const MY_VIEW_FILTER_ITEMS: FilterItem[] = [
  {
    value: 2,
    label: $localize`:@@pc-myDepartment:My Team`,
    subtitle: $localize`:@@pc-myDepartmentSubtitle:Costs I or my direct reports are responsible for`,
  },
  {
    value: 1,
    label: $localize`:@@pc-myCosts:My Costs`,
    subtitle: $localize`:@@pc-myCostsSubtitle:Only costs I am responsible for`,
  },
  {
    value: 4,
    label: $localize`:@@myProjects:My Projects`,
    subtitle: $localize`:@@myProjectsSubtitle:Only projects I'm assigned to`,
  },
  {
    value: 3,
    label: $localize`:@@pc-myFavorites:My favourite projects`,
    subtitle: $localize`:@@pc-myFavoritesSubtitle:Only costs from my favorite projects`,
  },
];

/** No "Alle" entry: everything ticked already means all, which is also the default. */
export const BEAUFTRAGT_FILTER_ITEMS: FilterItem[] = [
  { label: $localize`:@@commissioned:Commissioned`, value: 1 },
  { label: $localize`:@@notCommissioned:Not commissioned`, value: 2 },
];

export const AMOUNT_BASIS_FILTER_ITEMS: FilterItem[] = [
  { label: $localize`:@@gross:Gross`, value: 0 },
  { label: $localize`:@@nett:Nett`, value: 1 },
];

/**
 * Both ticked is "all", and so is nothing ticked: emptying a filter reads as an empty page for
 * no visible reason, which is the same call setDebitorFilter already makes for the Gesellschaft list.
 */
export function beauftragtSelectionToCode(selection: number[]): number {
  return selection?.length === 1 ? selection[0] : 0;
}

export function beauftragtCodeToSelection(code: number): number[] {
  return code === 1 || code === 2 ? [code] : [1, 2];
}

export function amountBasisSelectionToCode(selection: number[]): number {
  return selection?.length ? selection[0] : 0;
}

/**
 * Stored as "2,4". Before #4926 the key held "1"/"0", and before that the toggle's
 * "true"/"false" - both still read back, as does anything no longer offered (dropped).
 */
export function readStoredMyView(raw: string | null): number[] {
  if (raw === 'true') return [1];
  return (raw ?? '')
    .split(',')
    .map(Number)
    .filter((v) => MY_VIEW_FILTER_ITEMS.some((i) => i.value === v));
}
